import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import NumberBall from '@/components/base/NumberBall';
import QRShare from '@/components/feature/QRShare';
import DrawSimulator from './components/DrawSimulator';
import MobileBottomNav from '@/components/feature/MobileBottomNav';
import { usePageMeta } from '@/hooks/usePageMeta';
import { checkRank } from '@/lib/prizeRank';

interface SavedNumber {
  id: string;
  user_id: string;
  numbers: number[];
  bonus: number | null;
  generator_type: string;
  label: string | null;
  created_at: string;
}

interface DrawRecord {
  id: number;
  numbers: number[];
  bonus: number;
}

interface CheckResult {
  rank: number | null;
  matchedCount: number;
  bonusMatched: boolean;
  drawId: number;
}

const generatorLabels: Record<string, string> = {
  random: '랜덤 생성',
  lucky: '행운 생성',
  manual: '수동 입력',
};

const rankLabels: Record<number, string> = {
  1: '1등',
  2: '2등',
  3: '3등',
  4: '4등',
  5: '5등',
};

const rankColors: Record<number, string> = {
  1: 'bg-accent-500 text-accent-950',
  2: 'bg-primary-500 text-white',
  3: 'bg-secondary-700 text-white',
  4: 'bg-foreground-700 text-white',
  5: 'bg-foreground-600 text-white',
};

export default function MyNumbers() {
  const { user, profile } = useAuth();
  const [numbers, setNumbers] = useState<SavedNumber[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingLabel, setEditingLabel] = useState<string | null>(null);
  const [labelValue, setLabelValue] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [labelSavingId, setLabelSavingId] = useState<string | null>(null);

  const [latestDraw, setLatestDraw] = useState<DrawRecord | null>(null);
  const [checkResults, setCheckResults] = useState<Record<string, CheckResult>>({});
  const [checkingAll, setCheckingAll] = useState(false);

  const [qrModal, setQrModal] = useState<{ numbers: number[]; bonus: number | null; label: string | null } | null>(null);
  const [simModal, setSimModal] = useState<{ numbers: number[]; bonus: number | null; label: string | null } | null>(null);

  usePageMeta({
    title: '내 번호 관리 | LottoLab — 저장한 로또 번호 당첨 확인',
    description:
      'LottoLab 내 번호 페이지에서 저장한 로또 번호를 관리하고 최신 당첨번호와 비교해 당첨 여부를 바로 확인하세요. 랜덤·행운 생성기로 만든 나만의 번호를 보관하고 당첨 시뮬레이션까지 즐겨보세요.',
    keywords:
      '로또 번호 저장, 로또 당첨 확인, 내 로또 번호, 로또 번호 관리, 로또 번호 보관함, LottoLab, 로또랩',
    canonical: 'https://lottolab.app/my-numbers',
  });

  const fetchNumbers = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('saved_numbers')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setNumbers(data as SavedNumber[] || []);
    } catch (err: unknown) {
      if (import.meta.env.DEV) console.error('[MyNumbers] 번호 조회 실패:', err);
      setError('번호를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchLatestDraw = useCallback(async () => {
    const { data } = await supabase
      .from('draw_history')
      .select('id, numbers, bonus')
      .order('id', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (data) setLatestDraw(data as DrawRecord);
  }, []);

  useEffect(() => {
    fetchNumbers();
    fetchLatestDraw();
  }, [fetchNumbers, fetchLatestDraw]);

  const handleCheckSingle = (n: SavedNumber) => {
    if (!latestDraw) return;
    const result = checkRank(n.numbers, latestDraw.numbers, latestDraw.bonus);
    setCheckResults((prev) => ({
      ...prev,
      [n.id]: { ...result, drawId: latestDraw.id },
    }));
  };

  const handleCheckAll = () => {
    if (!latestDraw || numbers.length === 0) return;
    setCheckingAll(true);
    const results: Record<string, CheckResult> = {};
    numbers.forEach((n) => {
      const result = checkRank(n.numbers, latestDraw.numbers, latestDraw.bonus);
      results[n.id] = { ...result, drawId: latestDraw.id };
    });
    setCheckResults(results);
    setCheckingAll(false);
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    setError('');
    try {
      // 대상 행을 실제 삭제했는지 반환 행으로 확인(대상 없음도 실패 처리)
      const { data: deleted, error: deleteError } = await supabase
        .from('saved_numbers')
        .delete()
        .eq('id', id)
        .select('id')
        .maybeSingle();

      if (deleteError || !deleted) {
        if (import.meta.env.DEV) {
          console.error('[MyNumbers] 삭제 실패:', deleteError?.message ?? 'no row');
        }
        setError('삭제에 실패했습니다. 잠시 후 다시 시도해주세요.');
        return;
      }

      // DB 성공 후에만 로컬 목록에서 제거
      setNumbers((prev) => prev.filter((n) => n.id !== id));
      setDeleteConfirm(null);

      // 보조 활동 로그: 실패해도 삭제 성공을 뒤집지 않는다(개인정보 미기록)
      try {
        const { error: logError } = await supabase.from('user_activity_logs').insert({
          user_id: user!.id,
          action: 'delete_number',
          detail: {},
        });
        if (logError && import.meta.env.DEV) {
          console.error('[MyNumbers] 활동 로그 기록 실패:', logError.message);
        }
      } catch (logErr) {
        if (import.meta.env.DEV) {
          console.error('[MyNumbers] 활동 로그 기록 예외:', logErr);
        }
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('[MyNumbers] 삭제 예외:', err);
      }
      setError('삭제에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleUpdateLabel = async (id: string) => {
    const trimmed = labelValue.trim();
    setLabelSavingId(id);
    setError('');
    try {
      // 반환 행으로 대상 존재 여부를 확인해 "대상 없음"도 실패로 처리
      const { data: updated, error: updateError } = await supabase
        .from('saved_numbers')
        .update({ label: trimmed || null })
        .eq('id', id)
        .select('id')
        .maybeSingle();

      if (updateError || !updated) {
        if (import.meta.env.DEV) {
          console.error('[MyNumbers] 라벨 수정 실패:', updateError?.message ?? 'no row');
        }
        setError('라벨 수정에 실패했습니다. 잠시 후 다시 시도해주세요.');
        return;
      }

      // DB 성공 후에만 화면 라벨 변경
      setNumbers((prev) =>
        prev.map((n) => (n.id === id ? { ...n, label: trimmed || null } : n))
      );
      setEditingLabel(null);
      setLabelValue('');
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('[MyNumbers] 라벨 수정 예외:', err);
      }
      setError('라벨 수정에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setLabelSavingId(null);
    }
  };

  const startEditLabel = (n: SavedNumber) => {
    setEditingLabel(n.id);
    setLabelValue(n.label || '');
  };

  const hasCheckedResults = Object.keys(checkResults).length > 0;
  const anyWin = Object.values(checkResults).some((r) => r.rank !== null);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <div className="pt-20 pb-12">
        <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-heading text-2xl text-foreground-800 mb-1">내 번호</h1>
              <p className="text-foreground-500 text-sm">
                {profile?.nickname || user?.email?.split('@')[0]}님이 저장한 번호를 관리하세요.
              </p>
            </div>
            {latestDraw && numbers.length > 0 && (
              <button
                onClick={handleCheckAll}
                disabled={checkingAll}
                className="px-4 py-2 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-trophy-line mr-1"></i>
                {checkingAll ? '확인 중...' : `제 ${latestDraw.id}회 당첨 확인`}
              </button>
            )}
          </div>

          {/* Latest draw info */}
          {latestDraw && (
            <div className="mb-5 p-4 rounded-xl bg-background-100 border border-background-200 flex items-center gap-3 flex-wrap">
              <i className="ri-calendar-line text-foreground-400"></i>
              <span className="text-sm text-foreground-600">
                최신 회차: <strong className="text-foreground-800">제 {latestDraw.id}회</strong>
              </span>
              <span className="text-sm text-foreground-400">|</span>
              <span className="text-sm text-foreground-600">
                당첨번호:{' '}
                {latestDraw.numbers.map((n) => (
                  <span key={n} className="inline-flex w-6 h-6 rounded-full bg-primary-100 text-primary-700 text-[11px] items-center justify-center font-bold mx-0.5">
                    {n}
                  </span>
                ))}
                <span className="mx-1 text-foreground-300">+</span>
                <span className="inline-flex w-6 h-6 rounded-full bg-accent-100 text-accent-700 text-[11px] items-center justify-center font-bold mx-0.5">
                  {latestDraw.bonus}
                </span>
              </span>
            </div>
          )}

          {/* Win banner */}
          {hasCheckedResults && anyWin && (
            <div className="mb-5 p-4 rounded-xl bg-secondary-50 border border-secondary-200 flex items-center gap-3">
              <i className="ri-trophy-line text-secondary-600 text-lg"></i>
              <p className="text-sm text-secondary-700">
                축하합니다! 당첨된 번호가 있어요! 결과를 확인해보세요.
              </p>
            </div>
          )}

          {error && (
            <div className="mb-5 p-3 rounded-lg bg-accent-50 border border-accent-200 flex items-start gap-2">
              <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5"></i>
              <p className="text-accent-700 text-sm">{error}</p>
              <button onClick={fetchNumbers} className="ml-auto text-accent-600 underline text-sm cursor-pointer whitespace-nowrap">새로고침</button>
            </div>
          )}

          {loading ? (
            <div className="text-center py-20">
              <div className="w-10 h-10 mx-auto mb-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-foreground-500 text-sm">번호를 불러오는 중...</p>
            </div>
          ) : numbers.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-heart-line text-3xl text-foreground-300"></i>
              </div>
              <h2 className="font-heading text-xl text-foreground-600 mb-2">저장된 번호가 없어요</h2>
              <p className="text-foreground-500 text-sm mb-6">
                홈 화면에서 랜덤 또는 행운의 번호를 생성하고 저장해보세요!
              </p>
              <a
                href="/"
                onClick={(e) => { e.preventDefault(); window.REACT_APP_NAVIGATE('/'); }}
                className="inline-flex px-5 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
              >
                번호 생성하러 가기
              </a>
            </div>
          ) : (
            <div className="space-y-3">
              {numbers.map((n) => {
                const result = checkResults[n.id];
                return (
                  <div
                    key={n.id}
                    className={`bg-background-50 rounded-2xl p-5 border transition-all ${
                      result?.rank === 1 || result?.rank === 2
                        ? 'border-secondary-300 ring-1 ring-secondary-200'
                        : 'border-background-200 hover:border-background-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {/* Label */}
                        <div className="mb-3">
                          {editingLabel === n.id ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={labelValue}
                                onChange={(e) => setLabelValue(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateLabel(n.id); if (e.key === 'Escape') setEditingLabel(null); }}
                                placeholder="라벨 입력..."
                                className="px-3 py-1.5 rounded-lg border border-background-300 text-sm text-foreground-800 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 w-full max-w-[200px]"
                                autoFocus
                              />
                              <button onClick={() => handleUpdateLabel(n.id)} disabled={labelSavingId === n.id} className="text-primary-600 hover:text-primary-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                                <i className={labelSavingId === n.id ? 'ri-loader-4-line animate-spin' : 'ri-check-line'}></i>
                              </button>
                              <button onClick={() => setEditingLabel(null)} className="text-foreground-400 hover:text-foreground-600 cursor-pointer">
                                <i className="ri-close-line"></i>
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => startEditLabel(n)}
                              className="text-sm text-foreground-500 hover:text-foreground-700 cursor-pointer flex items-center gap-1"
                            >
                              {n.label || '라벨 없음'}
                              <i className="ri-edit-line text-xs"></i>
                            </button>
                          )}
                        </div>

                        {/* Numbers */}
                        <div className="flex flex-wrap gap-1.5">
                          {n.numbers.map((num, idx) => (
                            <NumberBall key={idx} number={num} size="sm" />
                          ))}
                          {n.bonus && (
                            <>
                              <span className="flex items-center text-foreground-300 text-sm mx-0.5">+</span>
                              <NumberBall key="bonus" number={n.bonus} size="sm" isBonus />
                            </>
                          )}
                        </div>

                        {/* Winning result */}
                        {result && (
                          <div className="mt-3 flex items-center gap-2 flex-wrap">
                            <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${
                              result.rank !== null
                                ? rankColors[result.rank]
                                : 'bg-background-200 text-foreground-500'
                            }`}>
                              {result.rank !== null ? rankLabels[result.rank] : '낙첨'}
                            </span>
                            <span className="text-xs text-foreground-500">
                              {result.matchedCount}개 일치
                              {result.bonusMatched ? ' + 보너스' : ''}
                            </span>
                            <span className="text-xs text-foreground-400">
                              (제 {result.drawId}회 기준)
                            </span>
                          </div>
                        )}

                        {/* Meta */}
                        <div className="flex items-center gap-3 mt-3">
                          <span className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded-full">
                            {generatorLabels[n.generator_type] || n.generator_type}
                          </span>
                          <span className="text-xs text-foreground-500">
                            {new Date(n.created_at).toLocaleDateString('ko-KR')}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => setQrModal({ numbers: n.numbers, bonus: n.bonus, label: n.label })}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-all cursor-pointer"
                          title="QR 공유"
                        >
                          <i className="ri-qr-code-line"></i>
                        </button>
                        <button
                          onClick={() => setSimModal({ numbers: n.numbers, bonus: n.bonus, label: n.label })}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-accent-600 hover:bg-accent-50 transition-all cursor-pointer"
                          title="당첨 시뮬레이션"
                        >
                          <i className="ri-history-line"></i>
                        </button>
                        {latestDraw && !result && (
                          <button
                            onClick={() => handleCheckSingle(n)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-secondary-600 hover:bg-secondary-50 transition-all cursor-pointer"
                            title="당첨 확인"
                          >
                            <i className="ri-trophy-line"></i>
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteConfirm(n.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-accent-600 hover:bg-accent-50 transition-all cursor-pointer"
                          title="삭제"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </div>

                    {/* Delete confirm */}
                    {deleteConfirm === n.id && (
                      <div className="mt-3 pt-3 border-t border-background-200 flex items-center gap-2">
                        <p className="text-sm text-foreground-500">정말 삭제할까요?</p>
                        <button
                          onClick={() => handleDelete(n.id)}
                          disabled={deletingId === n.id}
                          className="px-3 py-1.5 rounded-lg bg-accent-500 hover:bg-accent-600 text-accent-950 text-xs font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deletingId === n.id ? '삭제 중...' : '삭제'}
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          disabled={deletingId === n.id}
                          className="px-3 py-1.5 rounded-lg border border-background-300 text-foreground-500 text-xs hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          취소
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <Footer />
      <div className="md:hidden h-16"></div>
      <MobileBottomNav />

      {/* QR Modal */}
      {qrModal && (
        <QRShare
          numbers={qrModal.numbers}
          bonus={qrModal.bonus}
          label={qrModal.label}
          onClose={() => setQrModal(null)}
        />
      )}

      {simModal && (
        <DrawSimulator
          myNumbers={simModal.numbers}
          myBonus={simModal.bonus}
          label={simModal.label}
          onClose={() => setSimModal(null)}
        />
      )}
    </div>
  );
}